"use client";

import React, { useState } from "react";
import { Map, Compass, Mountain, Tent, ArrowRight, MapPin, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const westernStates = [{
  id: "WA",
  name: "Washington",
  reports: 42,
  path: "M120,20 L260,20 L260,110 L220,115 L120,115 L110,80 Z",
  label: {
    x: 180,
    y: 70
  }
}, {
  id: "MT",
  name: "Montana",
  reports: 35,
  path: "M260,40 L550,40 L540,200 L280,180 L260,110 Z",
  label: {
    x: 400,
    y: 110
  }
}, {
  id: "ID",
  name: "Idaho",
  reports: 28,
  path: "M260,20 L280,20 L290,110 L320,180 L260,180 L260,110 Z",
  path_corrected: "M260,20 L290,20 L300,120 L350,120 L340,230 L260,230 L260,115 Z",
  label: {
    x: 290,
    y: 100
  }
}, {
  id: "OR",
  name: "Oregon",
  reports: 56,
  path: "M110,115 L260,115 L260,230 L100,230 L90,180 Z",
  label: {
    x: 180,
    y: 170
  }
}, {
  id: "WY",
  name: "Wyoming",
  reports: 19,
  path: "M340,180 L540,180 L540,310 L340,310 Z",
  label: {
    x: 440,
    y: 245
  }
}, {
  id: "CA",
  name: "California",
  reports: 84,
  path: "M90,230 L200,230 L240,380 L280,500 L180,500 L60,350 Z",
  label: {
    x: 160,
    y: 360
  }
}, {
  id: "NV",
  name: "Nevada",
  reports: 31,
  path: "M200,230 L340,230 L340,420 L260,500 L240,380 Z",
  label: {
    x: 280,
    y: 320
  }
}, {
  id: "UT",
  name: "Utah",
  reports: 47,
  path: "M340,230 L440,230 L440,310 L480,310 L480,420 L340,420 Z",
  label: {
    x: 400,
    y: 320
  }
}, {
  id: "CO",
  name: "Colorado",
  reports: 62,
  path: "M440,310 L640,310 L640,420 L480,420 L480,310 Z",
  label: {
    x: 560,
    y: 365
  }
}, {
  id: "AZ",
  name: "Arizona",
  reports: 53,
  path: "M260,500 L480,500 L480,420 L340,420 L340,450 Z",
  path_corrected: "M280,500 L260,500 L340,420 L480,420 L480,600 L320,600 Z",
  label: {
    x: 400,
    y: 510
  }
}, {
  id: "NM",
  name: "New Mexico",
  reports: 25,
  path: "M480,420 L620,420 L620,600 L480,600 Z",
  label: {
    x: 550,
    y: 510
  }
}];
const correctedStates = westernStates.map(s => ({
  ...s,
  path: s.path_corrected || s.path
}));
export default function TerrainDiscoveryMap() {
  const [activeState, setActiveState] = useState<string | null>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const handleStateClick = (id: string) => {
    setActiveState(id === activeState ? null : id);
  };
  const activeStateData = correctedStates.find(s => s.id === activeState);
  return <section id="terrain-discovery-map" className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/shattered-island.png')] mix-blend-overlay"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`flex flex-col lg:flex-row gap-12 items-start transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {}
          <div className="w-full lg:w-2/3 relative">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-3xl md:text-4xl font-serif tracking-wide text-primary">
                  TERRAIN DISCOVERY
                </h2>
                <p className="text-muted-foreground mt-2 max-w-md">
                  Interactive expedition log. Select a region to access detailed trip reports, waypoints, and field notes.
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2 text-xs font-mono text-muted-foreground border border-border px-3 py-1 rounded-full bg-card/50">
                <Compass className="w-4 h-4 text-primary" />
                <span>NAV_MODE: ACTIVE</span>
              </div>
            </div>

            {}
            <div className="relative w-full aspect-[4/3] bg-card/30 rounded-xl border border-border/50 shadow-2xl overflow-hidden group">
              {}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]"></div>
              
              <TooltipProvider delayDuration={100}>
                <svg viewBox="0 0 700 650" className="w-full h-full drop-shadow-xl" style={{
                filter: "drop-shadow(0px 4px 10px rgba(0,0,0,0.3))"
              }}>
                  <defs>
                    <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                      <feGaussianBlur stdDeviation="4" result="blur" />
                      <feComposite in="SourceGraphic" in2="blur" operator="over" />
                    </filter>
                  </defs>

                  {correctedStates.map(state => {
                  const isActive = activeState === state.id;
                  return <Tooltip key={state.id}>
                        <TooltipTrigger asChild>
                          <g onClick={() => handleStateClick(state.id)} className="cursor-pointer transition-all duration-300" onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleStateClick(state.id);
                        }
                      }} tabIndex={0} role="button" aria-label={`Select ${state.name}`} aria-pressed={isActive}>
                            <path d={state.path} className={`
                                transition-all duration-300 ease-out
                                stroke-2 stroke-border/50
                                ${isActive ? "fill-primary/20 stroke-primary stroke-[3px]" : "fill-muted/10 hover:fill-accent/20 hover:stroke-accent"}
                              `} style={{
                          filter: isActive ? "url(#glow)" : "none"
                        }} />
                            {}
                            <text x={state.label.x} y={state.label.y} className={`
                                text-[14px] font-mono font-bold pointer-events-none select-none transition-colors duration-300
                                ${isActive ? "fill-primary" : "fill-muted-foreground/70"}
                              `} textAnchor="middle">
                              {state.id}
                            </text>
                            
                            {}
                            {state.reports > 0 && <circle cx={state.label.x + 15} cy={state.label.y - 5} r="3" className={isActive ? "fill-primary animate-pulse" : "fill-muted-foreground/30"} />}
                          </g>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="bg-card border-border text-card-foreground font-mono text-xs">
                          <div className="flex flex-col gap-1">
                            <span className="font-bold text-primary uppercase tracking-wider">{state.name}</span>
                            <span className="text-muted-foreground">{state.reports} Expeditions Logged</span>
                          </div>
                        </TooltipContent>
                      </Tooltip>;
                })}
                </svg>
              </TooltipProvider>

              {}
              <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm border border-border p-3 rounded-md text-xs font-mono text-muted-foreground shadow-lg">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 rounded-full bg-primary/20 border border-primary"></div>
                  <span>Selected Region</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-muted/20 border border-muted-foreground"></div>
                  <span>Available Terrain</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="w-full lg:w-1/3 flex flex-col h-full min-h-[500px]">
            <div className="bg-card border border-border rounded-xl p-6 h-full shadow-xl flex flex-col relative overflow-hidden">
              {}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-transparent"></div>

              <div className="mb-6">
                <h3 className="text-sm font-mono text-muted-foreground uppercase tracking-widest mb-1">
                  Mission Control
                </h3>
                <div className="h-px w-full bg-border mb-4"></div>
                
                {activeStateData ? <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex items-baseline justify-between mb-2">
                      <h2 className="text-4xl font-serif text-foreground">{activeStateData.name}</h2>
                      <span className="text-6xl font-bold text-primary/10 absolute top-4 right-4 pointer-events-none select-none">
                        {activeStateData.id}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-background/50 p-3 rounded border border-border/50">
                        <div className="text-xs text-muted-foreground uppercase mb-1">Expeditions</div>
                        <div className="text-2xl font-mono font-bold text-primary">{activeStateData.reports}</div>
                      </div>
                      <div className="bg-background/50 p-3 rounded border border-border/50">
                        <div className="text-xs text-muted-foreground uppercase mb-1">Status</div>
                        <div className="text-sm font-mono font-bold text-accent flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-accent animate-pulse"></div>
                          MAPPED
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 mb-8">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Accessing archive data for {activeStateData.name}. Terrain analysis indicates high variability. 
                        Check local reports for seasonal closures and trail conditions.
                      </p>
                      
                      {}
                      <div className="space-y-2 mt-4">
                        <h4 className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                          <Layers className="w-3 h-3" /> Recent Logs
                        </h4>
                        {[1, 2, 3].map(i => <div key={i} data-index={i} className="group flex items-center gap-3 p-2 hover:bg-accent/10 rounded transition-colors cursor-pointer border border-transparent hover:border-accent/20">
                            <div className="w-10 h-10 rounded bg-muted/20 flex items-center justify-center text-muted-foreground group-hover:text-accent transition-colors">
                              <Mountain className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate text-foreground group-hover:text-accent transition-colors">
                                {activeStateData.name} Backcountry Route #{i}0{i}
                              </div>
                              <div className="text-xs text-muted-foreground flex items-center gap-2">
                                <span>Oct 2023</span>
                                <span className="w-1 h-1 rounded-full bg-border"></span>
                                <span>Overland</span>
                              </div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2 group-hover:translate-x-0" />
                          </div>)}
                      </div>
                    </div>

                    <div className="mt-auto">
                      <Button className="w-full group" asChild>
                        <Link to="/TravelLogPage">
                          View All {activeStateData.name} Logs
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </div>
                  </div> : <div className="flex flex-col items-center justify-center h-[300px] text-center p-4 animate-in fade-in duration-500">
                    <div className="w-16 h-16 rounded-full bg-muted/10 flex items-center justify-center mb-4 text-muted-foreground">
                      <MapPin className="w-8 h-8 opacity-50" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">Select a Region</h3>
                    <p className="text-sm text-muted-foreground max-w-[250px]">
                      Interact with the map to filter the archive by state. View trip reports, waypoints, and gear loadouts.
                    </p>
                    <div className="mt-8 w-full max-w-[200px] h-1 bg-border/30 rounded-full overflow-hidden">
                      <div className="h-full bg-primary/50 w-1/3 animate-[shimmer_2s_infinite]"></div>
                    </div>
                  </div>}
              </div>
              
              {}
              <div className="mt-auto pt-4 border-t border-border flex justify-between items-center text-[10px] text-muted-foreground font-mono uppercase">
                <span>System: Online</span>
                <span>v2.4.0</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}